import { Typography, theme } from 'antd'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../Stores'
import { stripHtml } from '../../Helpers/stripHtml'
import { Message } from '../../Contracts'

const { Text } = Typography

interface TextMessageProps {
  message: Message
}

export function HighlightMatches({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>

  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escaped})`, 'gi')
  const parts = text.split(regex)

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} style={{ background: '#ffe58f', padding: 0, borderRadius: 2 }}>
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  )
}

const TextMessage = observer(({ message }: TextMessageProps) => {
  const { uiStore } = useStore()
  const { token } = theme.useToken()
  const query = uiStore.isMessageSearchOpen ? uiStore.messageSearchQuery : ''

  // Search mode: render plain text with highlights
  if (query) {
    return (
      <Text style={{ fontSize: 14, lineHeight: '20px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        <HighlightMatches text={stripHtml(message.content)} query={query} />
      </Text>
    )
  }

  // Check if content has HTML tags (new rich messages)
  const hasHtml = /<[a-z][\s\S]*>/i.test(message.content)

  if (!hasHtml) {
    // Plain text message (backward compat with existing messages)
    return (
      <Text style={{ fontSize: 14, lineHeight: '20px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {message.content}
      </Text>
    )
  }

  // Rich HTML message (CSS is in index.css, CSS vars provide theming)
  return (
    <div
      className="rich-message-content"
      style={{
        fontSize: 14,
        lineHeight: '20px',
        wordBreak: 'break-word',
        ['--rmc-code-bg' as string]: token.colorFillTertiary,
        ['--rmc-link-color' as string]: token.colorPrimary,
        ['--rmc-mention-bg' as string]: token.colorPrimaryBg,
        ['--rmc-mention-color' as string]: token.colorPrimary,
      }}
      dangerouslySetInnerHTML={{ __html: message.content }}
    />
  )
})

export default TextMessage
